import { useContext, useEffect, useCallback, useRef } from 'react';
import { css } from '@emotion/react';
import { connect } from 'react-redux';
import { RootState } from 'redux/schemas';
import { removeAlertMessage } from 'redux/actions/alert-messages';
import { AlertMessageProps as IAlertMessage } from 'redux/schemas/app/alert-message';
import {
  flashMessageMaxWidth,
  flashMessageOuterMarginSize,
  alertMessageZIndex,
} from 'styles/global_defaults/scaffolding';
import { tablet, smallDesktop, desktop } from 'styles/global_defaults/media-queries';
import uuid from 'react-uuid';
import { AngularServicesContext } from 'react-app';

import AlertMessage from './alert-message';
import AlertCloningMessage from './alert-cloning-message';

const DEFAULT_MESSAGE_DURATION = 10000;

type StateProps = {
  alertMessages: IAlertMessage[];
};

export type DispatchProps = {
  removeAlertMessage: typeof removeAlertMessage
};

type OwnProps = {};

// List of custom variants to original Alert Messages.
const alertComponentMap = {
  AlertCloningMessage,
};

export const AlertMessagesPanel = (props: StateProps & DispatchProps) => {
  const styles = css`
    &.alert-messages-panel{
      position: fixed;
      top: 0;
      left: 0;
      right: 0;
      text-align: left;
      z-index: ${alertMessageZIndex};

      ${tablet(css`
        margin: 0 ${flashMessageOuterMarginSize}px;
      `)};

      ${smallDesktop(css`
        margin: 0 ${flashMessageOuterMarginSize}px;
      `)};

      ${desktop(css`
        width: ${flashMessageMaxWidth}px;
        margin-left: auto;
        margin-right: auto;
      `)};
    }
  `;

  const { alertMessages, removeAlertMessage: removeMessage } = props;
  const angularServices = useContext(AngularServicesContext);

  // Keep a ref to always have access to the latest alertMessages
  const alertMessagesRef = useRef(alertMessages);
  alertMessagesRef.current = alertMessages;

  // Track which messages already have auto-dismiss scheduled
  const scheduledDismissals = useRef<Set<IAlertMessage>>(new Set());

  // Schedule auto-dismiss for messages with duration
  useEffect(() => {
    alertMessages.forEach((message) => {
      if (message.duration !== Infinity && !scheduledDismissals.current.has(message)) {
        scheduledDismissals.current.add(message);
        setTimeout(() => {
          // Compute current index at removal time, not at capture time
          const currentIndex = alertMessagesRef.current.indexOf(message);
          if (currentIndex !== -1) {
            removeMessage(currentIndex);
          }
          scheduledDismissals.current.delete(message);
        }, message.duration || DEFAULT_MESSAGE_DURATION);
      }
    });
  }, [alertMessages, removeMessage]);

  const closeNavigateMessages = useCallback(() => {
    // Collect messages to remove first (snapshot), then remove by reference
    const messagesToRemove = alertMessagesRef.current.filter(msg => msg.closeOnNavigate);
    messagesToRemove.forEach((message) => {
      const currentIndex = alertMessagesRef.current.indexOf(message);
      if (currentIndex !== -1) {
        removeMessage(currentIndex);
      }
    });
  }, [removeMessage]);

  // Listen for Angular UI-Router state changes (use $stateChangeStart to dismiss before transition)
  useEffect(() => {
    const $rootScope = angularServices?.$rootScope;
    if (!$rootScope) return undefined;

    const deregister = $rootScope.$on('$stateChangeStart', closeNavigateMessages);

    return () => {
      deregister();
    };
  }, [angularServices?.$rootScope, closeNavigateMessages]);

  return (
    <div css={styles} className='alert-messages-panel'>
      {props.alertMessages && props.alertMessages.map((message, i) => {
        if (message.customElement) {
          const Component = alertComponentMap[message.customElement.component];
          return (<Component key={uuid()} {...message} removeAlertMessage={() => props.removeAlertMessage(i)} />);
        }
        return (<AlertMessage key={uuid()} {...message} removeAlertMessage={() => props.removeAlertMessage(i)} />);
      })}
    </div>
  );
};

export default connect<StateProps, DispatchProps, OwnProps, RootState>((state, ownProps) => ({
  alertMessages: state.app.alertMessages,
  ...ownProps,
}), {
  removeAlertMessage,
})(AlertMessagesPanel);
